/**
 * ShiftView Component
 *
 * Displays detailed view of a shift with patrol timeline, or a guard/shift browser
 * when no shift is selected.
 */

import React, { useState, useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import moment from 'moment';

import { PatrolCard, BreakItem, ShiftHeader, ShiftBrowser, ShiftActionCards } from './components';
import { DeleteShiftDialog, ChangeGuardDialog, DuplicateDayDialog } from './dialogs';

const ShiftView = ({
  shift,
  onPatrolClick,
  onShiftSelect,
  onReset,
  onAddPatrol,
  onDuplicateShift,
  onReorderPatrols,
  onDeleteShift,
  onChangeShiftGuard,
  onBrowseGuardChange,
  onBrowseWeekChange,
  onSyncToCalendar,
  guards = [],
  allShifts = [],
  browseGuardId = null,
  browseGuardName = '',
  browseWeekStart: externalBrowseWeekStart,
  isMobile = false,
}) => {
  // Modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showChangeGuard, setShowChangeGuard] = useState(false);
  const [showDuplicateDayConfirm, setShowDuplicateDayConfirm] = useState(false);
  const [selectedNewGuard, setSelectedNewGuard] = useState(null);

  // Use external browse week if provided, otherwise manage locally
  const [localBrowseWeekStart, setLocalBrowseWeekStart] = useState(() => {
    const today = moment();
    return today.clone().startOf('isoWeek');
  });

  const browseWeekStart = externalBrowseWeekStart
    ? moment(externalBrowseWeekStart)
    : localBrowseWeekStart;

  const browseWeekEnd = useMemo(
    () => browseWeekStart.clone().endOf('isoWeek'),
    [browseWeekStart]
  );

  // Filter shifts for the selected guard and current browse week
  const filteredShifts = useMemo(() => {
    if (!browseGuardId || allShifts.length === 0) return [];

    return allShifts
      .filter((s) => {
        const shiftStart = moment(s.StartTime);
        return (
          String(s.ContactId) === String(browseGuardId) &&
          shiftStart.isSameOrAfter(browseWeekStart, 'day') &&
          shiftStart.isSameOrBefore(browseWeekEnd, 'day')
        );
      })
      .sort((a, b) => moment(a.StartTime).diff(moment(b.StartTime)));
  }, [browseGuardId, allShifts, browseWeekStart, browseWeekEnd]);

  // Week navigation handlers
  const handlePreviousWeek = useCallback(() => {
    const newWeekStart = browseWeekStart.clone().subtract(1, 'week');
    if (onBrowseWeekChange) {
      onBrowseWeekChange(newWeekStart.toDate());
    } else {
      setLocalBrowseWeekStart(newWeekStart);
    }
  }, [browseWeekStart, onBrowseWeekChange]);

  const handleNextWeek = useCallback(() => {
    const newWeekStart = browseWeekStart.clone().add(1, 'week');
    if (onBrowseWeekChange) {
      onBrowseWeekChange(newWeekStart.toDate());
    } else {
      setLocalBrowseWeekStart(newWeekStart);
    }
  }, [browseWeekStart, onBrowseWeekChange]);

  const handleShiftClick = useCallback(
    (clickedShift) => {
      onShiftSelect?.(clickedShift);
    },
    [onShiftSelect]
  );

  const handleReset = useCallback(() => {
    if (onBrowseGuardChange) {
      onBrowseGuardChange(null, '');
    }
    if (onBrowseWeekChange) {
      onBrowseWeekChange(moment().startOf('isoWeek').toDate());
    } else {
      setLocalBrowseWeekStart(moment().startOf('isoWeek'));
    }
    onReset?.();
  }, [onReset, onBrowseGuardChange, onBrowseWeekChange]);

  // Add patrol to shift handler
  const handleAddPatrolToShift = useCallback(() => {
    if (!shift || shift.patrols.length === 0) return;

    const lastPatrol = shift.patrols[shift.patrols.length - 1];
    const lastPatrolEnd = moment(lastPatrol.EndTime);

    onAddPatrol?.({
      CustomerName: lastPatrol.CustomerName,
      AddressName: lastPatrol.AddressName,
      ContactId: lastPatrol.ContactId,
      ContactName: lastPatrol.ContactName,
      date: lastPatrolEnd.format('YYYY-MM-DD'),
      startTime: lastPatrolEnd.format('HH:mm'),
      duration: '1',
    });
  }, [shift, onAddPatrol]);

  // Add new shift for browsed guard
  const handleAddNewShift = useCallback(() => {
    if (!browseGuardId) return;

    let latestPatrolEnd = null;
    if (filteredShifts.length > 0) {
      filteredShifts.forEach((shiftItem) => {
        const shiftEnd = moment(shiftItem.EndTime);
        if (!latestPatrolEnd || shiftEnd.isAfter(latestPatrolEnd)) {
          latestPatrolEnd = shiftEnd;
        }
      });
    }

    const autofillData = {
      ContactId: browseGuardId,
      ContactName: browseGuardName,
      CustomerName: '',
      AddressName: '',
      duration: '1',
    };

    if (latestPatrolEnd) {
      autofillData.date = latestPatrolEnd.clone().add(1, 'day').format('YYYY-MM-DD');
      autofillData.startTime = '';
    } else {
      autofillData.date = '';
      autofillData.startTime = '';
    }

    onAddPatrol?.(autofillData);
  }, [browseGuardId, browseGuardName, filteredShifts, onAddPatrol]);

  // Duplicate shift to next day
  const handleDuplicateDayClick = useCallback(() => {
    setShowDuplicateDayConfirm(true);
  }, []);

  const handleConfirmDuplicateDay = useCallback(() => {
    if (!shift || shift.patrols.length === 0) return;
    setShowDuplicateDayConfirm(false);

    const duplicatedPatrols = shift.patrols.map((patrol) => ({
      ContactName: patrol.ContactName,
      ContactId: patrol.ContactId,
      CustomerName: patrol.CustomerName,
      CustomerId: patrol.CustomerId,
      AddressName: patrol.AddressName,
      AddressId: patrol.AddressId,
      StartTime: moment(patrol.StartTime).add(1, 'day').toDate(),
      EndTime: moment(patrol.EndTime).add(1, 'day').toDate(),
    }));

    onDuplicateShift?.(duplicatedPatrols);
  }, [shift, onDuplicateShift]);

  const handleCancelDuplicateDay = useCallback(() => {
    setShowDuplicateDayConfirm(false);
  }, []);

  // Delete shift handlers
  const handleDeleteShiftClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleConfirmDeleteShift = useCallback(() => {
    if (!shift || !shift.patrols) return;
    setShowDeleteConfirm(false);
    onDeleteShift?.(shift.patrols);
  }, [shift, onDeleteShift]);

  const handleCancelDeleteShift = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  // Change guard handlers
  const handleChangeGuardClick = useCallback(() => {
    setSelectedNewGuard(null);
    setShowChangeGuard(true);
  }, []);

  const handleConfirmChangeGuard = useCallback(() => {
    if (!shift || !shift.patrols || !selectedNewGuard) return;
    setShowChangeGuard(false);
    onChangeShiftGuard?.(shift.patrols, selectedNewGuard);
    setSelectedNewGuard(null);
  }, [shift, selectedNewGuard, onChangeShiftGuard]);

  const handleCancelChangeGuard = useCallback(() => {
    setShowChangeGuard(false);
    setSelectedNewGuard(null);
  }, []);

  // Sort patrols by start time
  const sortedPatrols = useMemo(() => {
    if (!shift?.patrols) return [];
    return [...shift.patrols].sort(
      (a, b) => moment(a.StartTime).diff(moment(b.StartTime))
    );
  }, [shift?.patrols]);

  // Calculate breaks between consecutive patrols
  const getBreakAfterPatrol = useCallback((currentPatrol, nextPatrol) => {
    if (!nextPatrol) return null;

    const gapStart = moment(currentPatrol.EndTime);
    const gapEnd = moment(nextPatrol.StartTime);
    const gapMinutes = gapEnd.diff(gapStart, 'minutes');

    if (gapMinutes > 0 && gapMinutes < 60) {
      return {
        duration: gapMinutes,
        StartTime: gapStart,
        EndTime: gapEnd,
      };
    }
    return null;
  }, []);

  // Build unified list of shift items (patrols and breaks)
  const shiftItems = useMemo(() => {
    if (!shift?.patrols || sortedPatrols.length === 0) return [];

    const items = [];
    sortedPatrols.forEach((patrol, index) => {
      const duration = moment(patrol.EndTime).diff(moment(patrol.StartTime), 'minutes');
      items.push({
        type: 'patrol',
        id: patrol.ScheduleAssignmentId,
        patrol,
        duration,
      });

      const nextPatrol = sortedPatrols[index + 1];
      if (nextPatrol) {
        const breakInfo = getBreakAfterPatrol(patrol, nextPatrol);
        if (breakInfo) {
          items.push({
            type: 'break',
            id: `break-${patrol.ScheduleAssignmentId}-${nextPatrol.ScheduleAssignmentId}`,
            duration: breakInfo.duration,
          });
        }
      }
    });

    return items;
  }, [sortedPatrols, shift?.patrols, getBreakAfterPatrol]);

  // Handle swapping two adjacent items
  const handleSwap = useCallback(
    (itemIndex, direction) => {
      if (!shift || !onReorderPatrols) return;

      const targetIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
      if (targetIndex < 0 || targetIndex >= shiftItems.length) return;

      const newItems = [...shiftItems];
      [newItems[itemIndex], newItems[targetIndex]] = [newItems[targetIndex], newItems[itemIndex]];

      let currentTime = moment(shift.StartTime);

      const updatedPatrols = [];
      newItems.forEach((item) => {
        if (item.type === 'patrol') {
          const newStartTime = currentTime.clone();
          const newEndTime = currentTime.clone().add(item.duration, 'minutes');

          updatedPatrols.push({
            ...item.patrol,
            StartTime: newStartTime.toDate(),
            EndTime: newEndTime.toDate(),
          });

          currentTime = newEndTime;
        } else {
          currentTime = currentTime.clone().add(item.duration, 'minutes');
        }
      });

      onReorderPatrols(updatedPatrols);
    },
    [shift, shiftItems, onReorderPatrols]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {shift ? (
        <>
          <ShiftHeader
            shift={shift}
            onReset={handleReset}
            onDeleteShift={handleDeleteShiftClick}
            onChangeGuard={handleChangeGuardClick}
            onSyncToCalendar={onSyncToCalendar}
          />

          <Divider sx={{ marginBottom: 2 }} />

          {/* Patrol Cards with Timeline */}
          <Timeline
            sx={{
              padding: 0,
              margin: 0,
              '& .MuiTimelineItem-root:before': {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {shiftItems.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === shiftItems.length - 1;
              const patrolNumber = shiftItems.slice(0, index + 1).filter((i) => i.type === 'patrol').length;
              const showTimeline = shift.patrolCount > 1;

              if (item.type === 'patrol') {
                return (
                  <TimelineItem key={item.id}>
                    {showTimeline && (
                      <TimelineSeparator>
                        {!isFirst && <TimelineConnector />}
                        <TimelineDot color="primary" />
                        {!isLast && <TimelineConnector />}
                      </TimelineSeparator>
                    )}
                    <TimelineContent sx={{ py: 1, px: showTimeline ? 2 : 0 }}>
                      <PatrolCard
                        patrol={item.patrol}
                        patrolNumber={patrolNumber}
                        isFirst={isFirst}
                        isLast={isLast}
                        onPatrolClick={onPatrolClick}
                        onMoveUp={() => handleSwap(index, 'up')}
                        onMoveDown={() => handleSwap(index, 'down')}
                      />
                    </TimelineContent>
                  </TimelineItem>
                );
              }

              // Break item
              return (
                <TimelineItem key={item.id}>
                  {showTimeline && (
                    <TimelineSeparator>
                      {!isFirst && <TimelineConnector sx={{ bgcolor: 'divider' }} />}
                      <TimelineDot
                        variant="outlined"
                        sx={{
                          borderColor: 'divider',
                          width: 8,
                          height: 8,
                          minWidth: 8,
                        }}
                      />
                      {!isLast && <TimelineConnector sx={{ bgcolor: 'divider' }} />}
                    </TimelineSeparator>
                  )}
                  <TimelineContent sx={{ py: 1, px: showTimeline ? 2 : 0 }}>
                    <BreakItem
                      duration={item.duration}
                      isFirst={isFirst}
                      isLast={isLast}
                      onMoveUp={() => handleSwap(index, 'up')}
                      onMoveDown={() => handleSwap(index, 'down')}
                    />
                  </TimelineContent>
                </TimelineItem>
              );
            })}
          </Timeline>

          <ShiftActionCards
            shiftEndTime={shift.EndTime}
            shiftStartTime={shift.StartTime}
            onAddPatrol={handleAddPatrolToShift}
            onDuplicateShift={handleDuplicateDayClick}
          />
        </>
      ) : (
        <ShiftBrowser
          guards={guards}
          browseGuardId={browseGuardId}
          browseGuardName={browseGuardName}
          browseWeekStart={browseWeekStart}
          browseWeekEnd={browseWeekEnd}
          filteredShifts={filteredShifts}
          onBrowseGuardChange={onBrowseGuardChange}
          onPreviousWeek={handlePreviousWeek}
          onNextWeek={handleNextWeek}
          onShiftClick={handleShiftClick}
          onAddNewShift={handleAddNewShift}
        />
      )}

      {/* Dialogs */}
      <DeleteShiftDialog
        open={showDeleteConfirm}
        patrolCount={shift?.patrolCount || 0}
        onCancel={handleCancelDeleteShift}
        onConfirm={handleConfirmDeleteShift}
      />

      <ChangeGuardDialog
        open={showChangeGuard}
        patrolCount={shift?.patrolCount || 0}
        currentGuardId={shift?.ContactId}
        currentGuardName={shift?.ContactName}
        guards={guards}
        selectedGuard={selectedNewGuard}
        onGuardSelect={setSelectedNewGuard}
        onCancel={handleCancelChangeGuard}
        onConfirm={handleConfirmChangeGuard}
      />

      <DuplicateDayDialog
        open={showDuplicateDayConfirm}
        patrolCount={shift?.patrolCount || 0}
        guardName={shift?.ContactName}
        sourceDayLabel={moment(shift?.StartTime).format('ddd, MMM D')}
        targetDayLabel={moment(shift?.StartTime).add(1, 'day').format('ddd, MMM D')}
        onCancel={handleCancelDuplicateDay}
        onConfirm={handleConfirmDuplicateDay}
      />
    </Box>
  );
};

ShiftView.propTypes = {
  shift: PropTypes.shape({
    id: PropTypes.string,
    ContactName: PropTypes.string,
    patrols: PropTypes.arrayOf(
      PropTypes.shape({
        ScheduleAssignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        ContactName: PropTypes.string,
        ContactId: PropTypes.number,
        CustomerName: PropTypes.string,
        CustomerId: PropTypes.number,
        AddressName: PropTypes.string,
        AddressId: PropTypes.number,
        StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        Checklists: PropTypes.arrayOf(
          PropTypes.shape({
            ChecklistID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            Name: PropTypes.string,
            CustomerID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            AddressID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          })
        ),
      })
    ),
    StartTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    EndTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    totalDuration: PropTypes.number,
    patrolCount: PropTypes.number,
    locations: PropTypes.arrayOf(PropTypes.string),
    locationCount: PropTypes.number,
  }),
  onPatrolClick: PropTypes.func,
  onShiftSelect: PropTypes.func,
  onReset: PropTypes.func,
  onAddPatrol: PropTypes.func,
  onDuplicateShift: PropTypes.func,
  onReorderPatrols: PropTypes.func,
  onDeleteShift: PropTypes.func,
  onChangeShiftGuard: PropTypes.func,
  onBrowseGuardChange: PropTypes.func,
  onBrowseWeekChange: PropTypes.func,
  onSyncToCalendar: PropTypes.func,
  guards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  allShifts: PropTypes.array,
  browseGuardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  browseGuardName: PropTypes.string,
  browseWeekStart: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object]),
  isMobile: PropTypes.bool,
};

export default memo(ShiftView);
